import React, { useMemo } from 'react';
import { topicAttemptRowFromMap, topicRowAction } from '../utils/topicAttemptRow';
import useSectionPaperStatuses from '../hooks/useSectionPaperStatuses';
import {
  groupSectionPapers,
  sectionPaperSitting,
  sectionPaperTitle,
  SECTION_PAPER_DEFAULT_TIER,
} from '../utils/sectionPapers';

const TIER_LABELS = { beginnings: 'Beginnings', foundations: 'Foundations', beyond: 'Beyond' };

const PILL = {
  done: ['completed', 'Completed'],
  progress: ['in-progress', 'In progress'],
  none: ['not-started', 'Not started'],
};

/**
 * The section papers table of an exam's Practice Topics pages: standalone
 * single-section papers, one row each, never paired, grouped by section. `prefix` is
 * the workspace's CSS prefix ("ucat-ws"), so the table takes that workspace's own styles.
 */
const SectionPapersTable = ({
  exam,
  prefix,
  papers = [],
  completedList = [],
  isPartUnlocked,
  onStartSetPaper,
  onRequestUpgrade,
}) => {
  const dbPapers = useSectionPaperStatuses(exam, papers);

  const groups = useMemo(() => groupSectionPapers(papers), [papers]);

  if (!papers.length) return null;

  const rowFor = (paper) => {
    const tier = (paper.tier || SECTION_PAPER_DEFAULT_TIER).toLowerCase();
    const unlocked = isPartUnlocked ? isPartUnlocked(paper) : true;
    const action = topicRowAction(
      topicAttemptRowFromMap(dbPapers, paper.id),
      completedList.includes(paper.id)
    );
    const [pillClass, pillLabel] = PILL[action.status] || PILL.none;
    return (
      <tr key={paper.id}>
        <td data-label="Paper" className={`${prefix}-cell-strong`}>
          {sectionPaperTitle(paper)}
          {!unlocked && (
            <span style={{ marginLeft: 8, fontSize: 11, opacity: 0.6 }}>Locked</span>
          )}
        </td>
        <td data-label="Tier">{TIER_LABELS[tier] || tier}</td>
        <td data-label="Questions">{paper.question_count ?? '—'}</td>
        <td data-label="Time">{paper.duration || '—'}</td>
        <td data-label="Status">
          <span className={`${prefix}-pill ${pillClass}`}>{pillLabel}</span>
        </td>
        <td data-label="">
          <button
            type="button"
            className={`${prefix}-start-btn${action.status === 'none' ? '' : action.status === 'progress' ? ' mid' : ' ghost'}`}
            onClick={() => {
              if (!unlocked) {
                if (onRequestUpgrade) onRequestUpgrade(paper);
                return;
              }
              onStartSetPaper(
                exam,
                paper.id,
                paper.catalog_id || paper.id,
                sectionPaperSitting(exam, paper),
                { forceNew: action.forceNew }
              );
            }}
          >
            {action.label}
          </button>
        </td>
      </tr>
    );
  };

  return (
    <>
      {groups.map((group) => (
        <div
          key={group.label || 'section-papers'}
          className={`${prefix}-section`}
        >
          {group.label && groups.length > 1 && (
            <h2 className={`${prefix}-section-title`}>{group.label}</h2>
          )}
          <table className={`${prefix}-table`}>
            <thead>
              <tr>
                <th>Paper</th>
                <th>Tier</th>
                <th>Questions</th>
                <th>Time</th>
                <th>Status</th>
                <th />
              </tr>
            </thead>
            <tbody>{group.papers.map(rowFor)}</tbody>
          </table>
        </div>
      ))}
    </>
  );
};

export default SectionPapersTable;
